'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Activity {
  id: string;
  title: string;
  description: string;
  times: string;
  location: string;
  photo_url: string;
  visible: boolean;
}

const tagColors: Record<string, string> = {
  'Main Arena': 'bg-primary/10 text-primary',
  'Wrestling Arena': 'bg-red-100 text-red-600',
  'Dog Ring': 'bg-accent/10 text-accent',
  'Grounds': 'bg-accent/10 text-accent',
  'RC Track': 'bg-secondary/10 text-secondary',
  'Sports Area': 'bg-accent/10 text-accent',
  'Kids Zone': 'bg-primary/10 text-primary',
  'Activity Zone': 'bg-secondary/10 text-secondary',
};

const fallbackEmojis: Record<string, string> = {
  'Dinosaur Experience': '🦕',
  'Falconry Displays': '🦅',
  'Wrestling Shows': '🤼',
  'Dog Show': '🐕',
  'Land Train': '🚂',
  'RC Cars': '🏎️',
  'Sports Fair': '⚽',
  'Bouncy Castles': '🏰',
  'Axe Throwing': '🪓',
  'Laser Clay Shooting': '🎯',
};

export default function ActivityCards() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('activities')
          .select('*')
          .eq('visible', true)
          .order('sort_order', { ascending: true });
        if (!error && data) setActivities(data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchActivities();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">Family Activities</h2>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-2xl border border-border h-48 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Family Activities</h2>
      {activities?.map((act) => {
        const tagColor = tagColors[act?.location] || 'bg-primary/10 text-primary';
        const emoji = fallbackEmojis[act?.title] || '🎪';
        return (
          <div key={act?.id} className="bg-card rounded-2xl overflow-hidden card-shadow border border-border">
            {act?.photo_url ? (
              <div className="relative w-full h-36">
                <Image src={act.photo_url} alt={act.title} fill className="object-cover" />
              </div>
            ) : (
              <div className="img-placeholder h-36 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-5xl block mb-1">{emoji}</span>
                  <p className="text-muted-foreground/60 text-xs">Photo coming soon</p>
                </div>
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-base text-foreground">{act?.title}</h3>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0 ${tagColor}`}>
                  {act?.location}
                </span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">{act?.description}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs">🕐</span>
                <span className="text-xs font-semibold text-foreground">{act?.times}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}